// React Router 7 route tree — public marketing SPA. Every page renders inside
// the shared MarketingLayout (nav + outlet + footer). `/` is the D1 landing
// golden sample; /product + /modules are the D2 content pages; /ai + /pricing
// are the D3 content pages; /company + /legal are the D4 content pages. No auth
// guard — this surface is public; no data loaders — it is static.
//
// Frontend-audit MEDIUM-4: every route below `/` used to be an eager
// `Component:` import, landing the whole site (190.52kB gzip) in the entry
// chunk. Route-level code-splitting is an already-proven pattern in
// customer-web/operator-console (src/router.tsx lazyRoute()) — same idea
// here. The root route owns one bounded error boundary so a failed lazy chunk
// or page render keeps the marketing chrome and a recovery path. `/` itself
// stays eager — it's the first paint every visitor gets.

import type { ComponentType } from "react";
import { createBrowserRouter } from "react-router";

import { MarketingLayout } from "@/components/MarketingLayout";
import {
  MarketingRouteErrorBoundary,
  MarketingRouteHydrateFallback,
} from "@/components/route-boundaries";
import { LandingPage } from "@/routes/landing";
import { NotFoundPage } from "@/routes/not-found";

function lazyRoute<M>(load: () => Promise<M>, pick: (mod: M) => ComponentType) {
  return { lazy: async (): Promise<{ Component: ComponentType }> => ({ Component: pick(await load()) }) };
}

export const router = createBrowserRouter([
  {
    path: "/",
    Component: MarketingLayout,
    ErrorBoundary: MarketingRouteErrorBoundary,
    HydrateFallback: MarketingRouteHydrateFallback,
    children: [
      { index: true, Component: LandingPage },
      { path: "product", ...lazyRoute(() => import("@/routes/product"), (m) => m.ProductPage) },
      { path: "modules", ...lazyRoute(() => import("@/routes/modules"), (m) => m.ModulesPage) },
      {
        path: "modules/:moduleId",
        ...lazyRoute(() => import("@/routes/module-detail"), (m) => m.ModuleDetailPage),
      },
      { path: "ai", ...lazyRoute(() => import("@/routes/ai"), (m) => m.AiPage) },
      { path: "pricing", ...lazyRoute(() => import("@/routes/pricing"), (m) => m.PricingPage) },
      { path: "company", ...lazyRoute(() => import("@/routes/company"), (m) => m.CompanyPage) },
      { path: "legal", ...lazyRoute(() => import("@/routes/legal"), (m) => m.LegalPage) },
      // Spec 70 P9/S6 — public Help Center + contact (backed by /public/v1/*).
      { path: "help", ...lazyRoute(() => import("@/routes/help"), (m) => m.HelpCenterPage) },
      {
        path: "help/:slug",
        ...lazyRoute(() => import("@/routes/help-article"), (m) => m.HelpArticlePage),
      },
      { path: "contact", ...lazyRoute(() => import("@/routes/contact"), (m) => m.ContactPage) },
      // Must remain last: preserves the public chrome for any direct URL that
      // does not match an explicitly shipped marketing surface.
      { path: "*", Component: NotFoundPage },
    ],
  },
]);
